import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useCallback } from "react";
import { Edit2 } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { fetchClientConnections, toggleNetworkConnection, selectNetworksWithStatus, selectConnectionsLoading, } from "../../../redux/clientDataSlice";
import CommonLoader from "../../../common_components/CommonLoader";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import AdsNetworkAccount from "./AdsNetworkAccount";
import AffiliateNetworkAccount from "./AffiliateNetworkAccount";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import ConfirmActionModal from "../../../common_components/ConfirmActionModal";
/* ================= COMPONENT ================= */
const ConnectedNetworks = ({ open, onClose, clientId, clientName, onToggleNetwork, networkType = "paid_media", allowedNetworkIds = null, refetchClientList }) => {
    const dispatch = useDispatch();
    /* -------- REDUX SELECTORS -------- */
    const networks = useSelector((state) => selectNetworksWithStatus(clientId, networkType, allowedNetworkIds)(state));
    const isLoading = useSelector(selectConnectionsLoading);
    /* -------- LOCAL STATE (UI only) -------- */
    const [adsDrawerOpen, setAdsDrawerOpen] = useState(false);
    const [affiliateDrawerOpen, setAffiliateDrawerOpen] = useState(false);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [togglingNetwork, setTogglingNetwork] = useState(null);
    const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [networkToDisconnect, setNetworkToDisconnect] = useState(null);
    /* -------- FETCH CONNECTIONS ON MOUNT -------- */
    useEffect(() => {
        if (!clientId || !open)
            return;
        dispatch(fetchClientConnections({ clientId }));
    }, [dispatch, clientId, open]);
    /* -------- OPEN DRAWER HELPER -------- */
    const openDrawerForNetwork = useCallback((network) => {
        setSelectedNetwork(network);
        if (networkType === "paid_media" || network.id === "costingtons") {
            setAdsDrawerOpen(true);
        }
        else if (networkType === "affiliate") {
            setAffiliateDrawerOpen(true);
        }
    }, [networkType]);
    /* -------- HANDLERS -------- */
    const handleToggleNetwork = useCallback((networkId) => {
        const network = networks.find((n) => n.id === networkId);
        if (!network)
            return;
        const newConnectedState = !network.connected;
        // If turning ON -> Open drawer directly (no API call needed here)
        if (newConnectedState) {
            openDrawerForNetwork(network);
            return;
        }
        // If turning OFF -> Open Confirmation Modal
        setNetworkToDisconnect(network);
        setDisconnectModalOpen(true);
    }, [networks, openDrawerForNetwork]);
    const handleConfirmDisconnect = useCallback(async () => {
        if (!networkToDisconnect)
            return;
        const networkId = networkToDisconnect.id;
        setTogglingNetwork(networkId);
        try {
            // Make API call to disconnect
            const result = await dispatch(toggleNetworkConnection({
                clientId,
                networkId,
                connected: false,
            })).unwrap();
            // Callback to parent if provided
            if (onToggleNetwork) {
                onToggleNetwork(networkId, result.connected, 0);
            }
            // Refetch connections after disconnecting
            dispatch(fetchClientConnections({ clientId }));
            setDisconnectModalOpen(false);
            setNetworkToDisconnect(null);
        }
        catch (error) {
            console.error("Failed to disconnect network:", error);
            setDisconnectModalOpen(false);
            setNetworkToDisconnect(null);
        }
        finally {
            setTogglingNetwork(null);
        }
    }, [dispatch, clientId, networkToDisconnect, onToggleNetwork]);
    const handleEditNetwork = useCallback((networkId, e) => {
        e.stopPropagation();
        const network = networks.find((n) => n.id === networkId);
        if (!network)
            return;
        openDrawerForNetwork(network);
    }, [networks, openDrawerForNetwork]);
    const handleCloseDrawers = useCallback(() => {
        setAdsDrawerOpen(false);
        setAffiliateDrawerOpen(false);
        setSelectedNetwork(null);
        // Refetch after drawer closes (in case changes were made)
        dispatch(fetchClientConnections({ clientId }));
        refetchClientList?.();
    }, [dispatch, clientId]);
    /* -------- EARLY RETURN -------- */
    if (!open)
        return null;
    /* -------- DERIVED VALUES -------- */
    const sidebarTitle = networkType === "paid_media" ? "Paid Media Integrations" : "Affiliate Networks";
    const switchColor = networkType === "paid_media" ? "#ff008e" : "#7b1fa2";
    /* -------- RENDER -------- */
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: sidebarTitle, subtitle: clientName, headerActions: null, isLoading: isLoading, width: "md", children: _jsx("div", { className: "networks-list", children: isLoading ? (_jsx("div", { className: "loading-spinners", children: _jsx(CommonLoader, { className: "table-loader" }) })) : networks.length === 0 ? (_jsx("div", { className: "no-networks-message", children: _jsx("p", { children: "No networks connected. Use the options above to connect a network." }) })) : (networks.map((network) => (_jsx(NetworkCard, { network: network, isToggling: togglingNetwork === network.id, switchColor: switchColor, onToggle: handleToggleNetwork, onEdit: handleEditNetwork }, network.id)))) }) }), adsDrawerOpen && (_jsx(AdsNetworkAccount, { open: adsDrawerOpen, onClose: handleCloseDrawers, network: selectedNetwork, clientId: clientId, clientName: clientName })), affiliateDrawerOpen && (_jsx(AffiliateNetworkAccount, { open: affiliateDrawerOpen, onClose: handleCloseDrawers, network: selectedNetwork, clientId: clientId, clientName: clientName, refetchClientList: refetchClientList })), _jsx(ConfirmActionModal, { open: disconnectModalOpen, title: "Disconnect Network", description: `This action will stop data syncing and disable active ${networkToDisconnect?.name} integrations. Existing reporting data will remain available.
`, confirmText: "Disconnect", cancelText: "Cancel", onConfirm: handleConfirmDisconnect, onClose: () => {
                    setDisconnectModalOpen(false);
                    setNetworkToDisconnect(null);
                }, loading: togglingNetwork === networkToDisconnect?.id, isWarning: true, isDeleteIcon: false })] }));
};
const NetworkCard = React.memo(({ network, isToggling, switchColor, onToggle, onEdit }) => {
    const inactiveAccounts = network.inactiveAccounts ?? 0;
    const statusParts = [];
    if (network.accounts > 0) {
        statusParts.push(`${network.accounts} Account${network.accounts !== 1 ? "s" : ""} Connected`);
    }
    if (inactiveAccounts > 0) {
        statusParts.push(`${inactiveAccounts} Inactive`);
    }
    const statusText = statusParts.length ? statusParts.join(" · ") : "Not Connected";
    // Show Edit when there are active OR inactive connections to view/manage.
    const canEdit = network.connected || inactiveAccounts > 0;
    return (_jsxs("div", { className: `network-card ${network.connected ? "connected" : ""}`, children: [_jsxs("div", { className: "left-section", children: [_jsx("div", { className: "brand-icon", children: _jsx("img", { className: "brand-img", src: network.icon, alt: network.name, loading: "lazy" }) }), _jsxs("div", { className: "text-block", children: [_jsx("div", { className: "network-name", children: network.name }), _jsx("div", { className: `network-status ${network.connected ? "on" : "off"}`, children: statusText })] })] }), _jsxs("div", { className: "right-section", children: [canEdit && (_jsxs(_Fragment, { children: [_jsxs("button", { className: "edit-link", onClick: (e) => onEdit(network.id, e), disabled: isToggling, children: [_jsx(Edit2, { size: 14 }), _jsx("span", { children: "Edit" })] }), _jsx("div", { className: "divider" })] })), _jsx(HoverTooltip, { title: network.connected ? "Disable the network across all modules." : "Enable the network across all modules.", children: _jsx("div", { className: "compare-toggle-container normal-switch", children: _jsx(FormControlLabel, { className: "compare-label", control: _jsx(Switch, { className: "compare-switch", size: "medium", checked: network.connected, onChange: () => onToggle(network.id), disabled: isToggling, sx: {
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: switchColor,
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: switchColor,
                                        },
                                    } }), label: "", sx: { flex: 1, m: 0 } }) }) })] })] }));
});
NetworkCard.displayName = "NetworkCard";
export default ConnectedNetworks;
